import {SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useRef, useState} from 'react';

import {
  blockAccessTypes,
  blockModelMessages
} from '../../constants/blockPageConstatnts';
import InformativeText from '../../constants/InformativeText';
import {SampleExcel} from '../../constants/SampleExcel';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import franchiseAPI from '../../services/modules/AM/franchise';
import listingFranchiseAPI from '../../services/modules/AM/listingFranchise';
import cityJSON from '../../services/modules/JSON/cityJSON';
import country from '../../services/modules/JSON/country';
import statesJSON from '../../services/modules/JSON/statesJSON';
import getTotalUserCount from '../../services/modules/User/totalUserCount';
import useCompanyStore, {
  accessModule,
  countModule
} from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import mapAndFilter from '../../utils/mapCountry';
import {
  // getAvailableDataLimit,
  getModuleDataLimit,
  hasMaxLimitReached
} from '../../utils/SubscriptionHelper';
import BlockAccessPage from '../BlockAccess/BlockAccessPage';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {
  franchiseKey,
  franchiseLabel
} from '../Forms/Constants/IP/franchiseKeyLabel';
import FranchiseForm from '../Forms/FranchiseForm';
import {emissionUnits} from '../Forms/interface/unitChange';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import TableTitleHeader from './Utils/TableTitleHeader';

const FranchiseTable: React.FC<{
  selectedYear: string;
  isConfigurator?: boolean;
  isAssetManager?: boolean;
}> = ({selectedYear, isConfigurator, isAssetManager = false}) => {
  interface DataType {
    _id: string;
    assetName: string;
    totalSpace: number;
    rentedFrom: string;
    rentedTo: string;
    totalEmission: number;
    cost: number;
    driven: number;
    country: string;
    area: number;
    comment: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);

  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const countryOption = useJSONStore.getState().country;
  const stateOption = useJSONStore.getState().states;
  const cityOption = useJSONStore.getState().city;
  const companyId = useCompanyStore.getState().companyId;
  const {area} = useCompanyStore.getState().preUnits;
  const [showModelInfo, setShowModelInfo] = useState(false);

  useEffect(() => {
    if (selectedYear) {
      setIsRefresh(true);
      listingFranchiseAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}`,
        isAssetManager ? 'dataCollection' : 'valueChain'
      )
        .then((res) => {
          setDataSource(
            res.map((data: any) => {
              return {
                ...data,
                fullAddress:
                  data?.[franchiseKey.ADDRESS] && data?.[franchiseKey.COUNTRY]
                    ? `${data?.[franchiseKey.ADDRESS]}, ${cityOption.find(
                        (item: any) => {
                          return item.value === data?.[franchiseKey.CITY];
                        }
                      )?.label}, ${stateOption.find((item: any) => {
                        return item.value === data?.[franchiseKey.STATE];
                      })?.label}, ${countryOption.find((item: any) => {
                        return item.value === data?.[franchiseKey.COUNTRY];
                      })?.label}, ${data.pincode}`
                    : '-'
              };
            }) ?? []
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear]);

  const franchiseKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: franchiseKey.FRANCHISE_NAME,
      label: franchiseLabel.FRANCHISE_NAME,
      type: objTypes.string
    },
    {
      key: franchiseKey.FIRST_NAME,
      label: franchiseLabel.FIRST_NAME,
      type: objTypes.string
    },
    {
      key: franchiseKey.LAST_NAME,
      label: franchiseLabel.LAST_NAME,
      type: objTypes.string
    },
    {
      key: franchiseKey.MOBILE,
      label: franchiseLabel.MOBILE,
      type: objTypes.string
    },
    {
      key: franchiseKey.EMAIL,
      label: franchiseLabel.EMAIL,
      type: objTypes.string
    },
    {
      key: franchiseKey.ADDRESS,
      label: franchiseLabel.ADDRESS,
      type: objTypes.string
    },
    {
      key: franchiseKey.COUNTRY,
      label: franchiseLabel.COUNTRY,
      type: objTypes.json,
      format: countryOption
    },
    {
      key: franchiseKey.STATE,
      label: franchiseLabel.STATE,
      type: objTypes.json,
      format: stateOption
    },
    {
      key: franchiseKey.CITY,
      label: franchiseLabel.CITY,
      type: objTypes.json,
      format: cityOption
    },
    {
      key: franchiseKey.PINCODE,
      label: franchiseLabel.PINCODE,
      type: objTypes.string
    },
    {
      key: franchiseKey.NO_EMP,
      label: franchiseLabel.NO_EMP,
      type: objTypes.number
    },
    {
      key: franchiseKey.FLOOR_SPACE,
      label: franchiseLabel.FLOOR_SPACE,
      type: objTypes.number,
      unit: area
    },
    {
      key: franchiseKey.GST,
      label: franchiseLabel.GST,
      type: objTypes.string,
      relatedKey: franchiseKey.COUNTRY
    },
    ...(isAssetManager
      ? [
          {
            key: franchiseKey.TOTAL_EMISSION,
            label: franchiseLabel.TOTAL_EMISSION,
            type: objTypes.number
          }
        ]
      : []),
    ...(hasUserRole(UserRole.Checker_Role) ||
    (hasUserRole(UserRole.Both_role) && !isConfigurator)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      Promise.all([
        listingFranchiseAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}`,
          isAssetManager ? 'dataCollection' : 'valueChain'
        ),
        getTotalUserCount()
      ])
        .then(([res]) => {
          setDataSource(
            res.map((data: any) => {
              return {
                ...data,
                fullAddress:
                  data?.[franchiseKey.ADDRESS] && data?.[franchiseKey.COUNTRY]
                    ? `${data?.[franchiseKey.ADDRESS]}, ${cityOption.find(
                        (item: any) => {
                          return item.value === data?.[franchiseKey.CITY];
                        }
                      )?.label}, ${stateOption.find((item: any) => {
                        return item.value === data?.[franchiseKey.STATE];
                      })?.label}, ${countryOption.find((item: any) => {
                        return item.value === data?.[franchiseKey.COUNTRY];
                      })?.label}, ${data.pincode}`
                    : '-'
              };
            }) ?? []
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([country(), statesJSON(), cityJSON(), getTotalUserCount()])
      .catch(() => {})
      .finally(() => {
        //  setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  const handleDelete = (_id: string[] | string) => {
    franchiseAPI(APITypeEnum.DELETE_ALL, {
      franchise: _id,
      year: selectedYear
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof DataType
  ): defaultCol & ColumnType<DataType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value?.toString().toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = [
    {
      title: franchiseLabel.FRANCHISE_NAME,
      dataIndex: franchiseKey.FRANCHISE_NAME,
      ...SearchFilter(
        franchiseLabel.FRANCHISE_NAME,
        franchiseKey.FRANCHISE_NAME as keyof DataType
      )
    },
    {
      title: franchiseLabel.FLOOR_SPACE,
      dataIndex: franchiseKey.FLOOR_SPACE,
      //   width: '120px',
      editable: true,
      type: 'number',
      render: (value) => {
        return value ? `${formatIndian(value)} ${area}` : '-';
      }
    },
    {
      title: franchiseLabel.NO_EMP,
      dataIndex: franchiseKey.NO_EMP,
      //   width: '120px',
      type: 'number',
      render: (value) => {
        return formatIndian(value);
      }
    },
    {
      title: franchiseLabel.ADDRESS,
      dataIndex: 'fullAddress',
      ...SearchFilter(franchiseLabel.ADDRESS, 'fullAddress' as keyof DataType),
      render: (value) => {
        return value || '-';
      }
      //   width: '120px',
    },
    {
      title: franchiseLabel.COUNTRY,
      dataIndex: franchiseKey.COUNTRY,
      editable: true,
      type: 'select',
      filterer: countryOption,
      filters: mapAndFilter(
        dataSource,
        countryOption,
        franchiseKey.COUNTRY
      ).map((item) => {
        return {text: item.label, value: item.value};
      }),
      onFilter: (value, record) => {
        return record?.country === value;
      },
      render: (value) => {
        return (
          <>
            {countryOption.find((item) => {
              return item.value === value;
            })?.label ?? '-'}
          </>
        );
      }
    },
    // {
    //   title: 'Emission',
    //   dataIndex: TF,
    //   editable: true,
    //   type: 'select',
    //   // ...sorter(FuelKey.QUANTITY_TF as keyof DataType),
    //   onFilter: (value, record) => {
    //     return returnFilter(record, value);
    //   },
    //   render: (value) => {
    //     const mainData = value?.find((item: any) => {
    //       return item.type === 'Type1';
    //     });
    //     // return <TimeFrameRender value={value} dataKey={FuelKey.QUANTITY_TF} />;

    //     return (
    //       <div
    //         onClick={() => {
    //           setOpenDisplayModal(true);
    //           setOpenDisplayModalData(mainData);
    //           setOpenDisplayModalType({
    //             title: 'Emissions',
    //             dataKey: 'Type1'
    //           });
    //         }}
    //         style={{
    //           textDecorationLine: 'underline',
    //           pointerEvents: 'auto',
    //           cursor: 'pointer'
    //         }}
    //       >
    //         <div>{`${String(formatIndian(sumValue(mainData?.data)))} ${
    //           mainData?.unit ?? ''
    //         }`}</div>
    //       </div>
    //     );
    //   }
    // }
    ...(isAssetManager
      ? [
          {
            title: franchiseLabel.TOTAL_EMISSION,
            dataIndex: franchiseKey.TOTAL_EMISSION,
            editable: true,
            // ...sorter(upstreamKey.TOTAL_FUEL_EMISSIONS as keyof DataType),
            //   width: '100px',
            // filterer: monthlyYearly,            //   return {text: item.label, value: item.value};
            // }),
            // onFilter: (value, record) => {
            //   return record.totalEmission === value;
            // },
            render: (value: any) => {
              return `${formatIndian(value)} ${emissionUnits.T_CO2e}`;
            }
          }
        ]
      : []),
    ...((hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)) &&
    !isConfigurator &&
    !isAssetManager
      ? [
          {
            title: makeCheckKeyLabel.STATUS_COMMENTS.label,
            dataIndex: 'status',
            editable: true,
            type: 'select',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(value);

              return (
                <div
                  style={{
                    fontWeight: 700,
                    color: colorText.color,
                    cursor: 'pointer'
                  }}
                >
                  {colorText.text}{' '}
                  {value === 'rejected' && record?.comment && (
                    <span className="customComment"> - {record.comment}</span>
                  )}
                </div>
              );
            }
          }
        ]
      : []),
    ...(hasUserRole(UserRole.Checker_Role) ||
    (hasUserRole(UserRole.Both_role) && !isConfigurator)
      ? [
          {
            title: makeCheckKeyLabel.STATUS.label,
            // dataIndex: 'status',
            editable: true,
            type: 'select',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(statusEm.ACCEPTED);

              return (
                <Tag
                  onClick={() => {
                    // setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  color={colorText.color}
                  style={{
                    fontWeight: 700
                  }}
                >
                  {colorText.text}
                </Tag>
              );
            }
          }
        ]
      : []),

    // ...(hasUserRole(UserRole.Checker_Role) ||
    // (hasUserRole(UserRole.Both_role) && !isConfigurator)
    //   ? [
    //       {
    //         title: FullInfo.title,
    //         dataIndex: 'FullInfo',
    //         editable: true,
    //         type: 'select',
    //         render: (_: any, record: any) => {
    //           return (
    //             <Button
    //               onClick={() => {
    //                 setShowModelInfo(true);
    //                 setEditData(record);
    //                 setIsEdit(true);
    //               }}
    //               shape="circle"
    //               style={{
    //                 marginRight: 16,
    //                 cursor: 'pointer',
    //                 color: '#435B37',
    //                 background: '#F5FBF3',
    //                 border: 0
    //               }}
    //             >
    //               <ExclamationOutlined />
    //             </Button>
    //           );
    //         }
    //       }
    //     ]
    //   : []),
    ...((isConfigurator ? !hasUserRole(UserRole.View_Only) : false)
      ? [
          {
            title: Title.actionTitle,
            dataIndex: 'operation',
            type: '' as const,
            fixed: 'right' as const,
            width: '200px',
            render: (value: any, record: any) =>
              dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                  isConfigurator={isConfigurator}
                />
              ) : null
          }
        ]
      : [])
  ];

  return (
    <div>
      <TableTitleHeader
        title="Franchise Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Franchise'
        }}
        tooltip={InformativeText.AM.franchise}
        sampleDownload={{href: SampleExcel.AssetFranchise}}
        jobs={{
          setShowJobs,
          uploadType: uploadTypes.AssetFranchise
        }}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        onlyYear={true}
        isTable={true}
        showAddOnly={!isConfigurator}
        onlyUpload={!isConfigurator}
        shouldEdit={
          isConfigurator
            ? !hasUserRole(UserRole.View_Only)
            : hasUserRole(UserRole.Maker_Role) ||
              hasUserRole(UserRole.Both_role)
        }
        blockAddition={hasMaxLimitReached(countModule.externalStakeholders)}
        shouldDisable={refresh}
        // accessModuleType={accessModule.externalStakeholders}
        // accessModuleDataLimit={getAvailableDataLimit(
        //   countModule.externalStakeholders
        // )}
      />

      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={
          isConfigurator
            ? hasUserRole(UserRole.Maker_Role) ||
              hasUserRole(UserRole.Both_role)
              ? {
                  type: 'checkbox',
                  onChange: (
                    selectedKey: React.Key[],
                    selectedRow: DataType[]
                  ) => {
                    setSelectedRowKeys(
                      selectedRow.map((item) => {
                        return item._id;
                      })
                    );
                  },
                  selectedRowKeys
                }
              : undefined
            : undefined
        }
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />

      {hasMaxLimitReached(countModule.externalStakeholders) ? (
        <div style={{marginTop: 15, marginBottom: 10}}>
          <BlockAccessPage
            blockedType={blockAccessTypes.PARTIAL}
            messageTitle={
              blockModelMessages.externalStakeholder.title.limitReached
            }
            messageDescription={blockModelMessages.externalStakeholder.description.limitReached(
              getModuleDataLimit(accessModule.externalStakeholders)
            )}
          />
        </div>
      ) : null}

      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
        type={uploadTypes.AssetFranchise}
        details={`${null}/${selectedYear}`}
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        setRefresh={setRefresh}
        data={isEdit ? editData : undefined}
        FormComponent={<FranchiseForm data={isEdit ? editData : undefined} />}
        API={franchiseAPI}
        idKey={`${companyId}/${selectedYear}`}
        table={true}
        onlyYear={true}
        buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
        stepsTitle={['General Details', 'Franchise Details']}
        formPreview={franchiseKeyLabel}
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={franchiseKeyLabel} />
        }
        API={franchiseAPI}
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        // formPreview={franchiseKeyLabel}
        isConfigurator={isConfigurator}
      />
    </div>
  );
};

export default FranchiseTable;
